import type { FactionId, HexOwner, RunEnding, RunState, RunStatus } from "../types/state";
import { landCount, ownedBy, unitsOf } from "./board";
import { heldFactions } from "./seats";
import { allied, hostile, kingdomsOfSide, sameOwner, sidesOfRun } from "./sides";

/**
 * How a run ends: elimination, surrender, the clock and sudden death, and each side's outcome. A
 * side (K6) is its kingdoms together: out when every one of them is, its land their land summed;
 * without `rules.sides` every kingdom is a side of its own, as before K6.
 */

export function remainingTicks(state: RunState): number {
  return Math.max(0, state.rules.runLengthTicks - state.tick);
}

/** A faction with no hex, or with no city and no unit, is out: nothing it has can act again. */
export function eliminated(state: RunState, faction: FactionId): boolean {
  const hexes = ownedBy(state, faction);
  if (hexes.length === 0) {
    return true;
  }
  const hasCity = hexes.some((hex) => hex.structure === "city" && !hex.razed);
  return !hasCity && unitsOf(state, faction).length === 0;
}

/** A side is out when every kingdom of it is (K6). */
export function sideEliminated(state: RunState, side: readonly FactionId[]): boolean {
  return side.every((kingdom) => eliminated(state, kingdom));
}

/** The hexes a side holds: its kingdoms' land summed, the score a verdict by side compares (K6). */
export function sideLand(state: RunState, side: readonly FactionId[]): number {
  return side.reduce((sum, kingdom) => sum + landCount(state, kingdom), 0);
}

/** Every side but the player's, in the order of the sides (K6), each kingdom alone without sides. */
export function rivalSidesOf(state: RunState): (readonly FactionId[])[] {
  return sidesOfRun(state).filter(
    (side) => !side.some((kingdom) => allied(state.rules, kingdom, "player")),
  );
}

/**
 * Win/lose from the player's side's view. Elimination ends the run at once; otherwise the clock
 * decides, or, under sudden death, the first hex to change hands once it is on (A57):
 * `landChanged` says whether one did this tick.
 */
function evaluateStatus(state: RunState, landChanged: boolean): RunStatus {
  const own = kingdomsOfSide(state, "player");
  if (sideEliminated(state, own)) {
    return "lost";
  }
  const standing = rivalSidesOf(state).filter((side) => !sideEliminated(state, side));
  if (standing.length === 0 || state.enemyDespairTicks >= state.rules.surrenderTicks) {
    return "won";
  }
  const player = sideLand(state, own);
  const leadsEveryRival = (): boolean => standing.every((side) => player > sideLand(state, side));
  if (state.tick >= state.rules.runLengthTicks) {
    return leadsEveryRival() ? "won" : "lost";
  }
  if (landChanged && suddenDeathOn(state)) {
    if (leadsEveryRival()) {
      return "won";
    }
    if (standing.some((side) => sideLand(state, side) > player)) {
      return "lost";
    }
  }
  return "running";
}

/** Sudden death is on: the rule is set, the run still runs and the tick has come (A57). */
export function suddenDeathOn(state: RunState): boolean {
  return (
    state.status === "running" &&
    state.rules.suddenDeathTick > 0 &&
    state.tick >= state.rules.suddenDeathTick
  );
}

/** Whether any hex changed hands since `owners` was read off the same hex list, in order (A57). */
export function landChangedSince(state: RunState, owners: readonly HexOwner[]): boolean {
  return state.hexes.some((hex, index) => !sameOwner(hex.owner, owners[index]));
}

/**
 * How a finished run ended, read off the state alone: elimination (a side with nothing left),
 * the enemy's surrender, the clock, or, before the clock with both sides standing, sudden
 * death (A57). Undefined while the run runs. The HUD titles and `run_end` name it.
 */
export function runEnding(state: RunState): RunEnding | undefined {
  if (state.status === "running") {
    return undefined;
  }
  if (
    sideEliminated(state, kingdomsOfSide(state, "player")) ||
    rivalSidesOf(state).every((side) => sideEliminated(state, side))
  ) {
    return "elimination";
  }
  if (enemySurrendered(state)) {
    return "surrender";
  }
  return state.tick >= state.rules.runLengthTicks ? "clock" : "suddenDeath";
}

/**
 * The run's outcome for one kingdom (B3.3), its side's (K6). `status` is the player's side's, as
 * it always was the player's kingdom's; for a rival side it is turned round, except that a level
 * clock ("a tie loses") loses for both. Elimination, surrender and sudden death leave no tie: one
 * side ended ahead.
 */
export function outcomeFor(state: RunState, faction: FactionId): RunStatus {
  if (state.status === "running" || allied(state.rules, faction, "player")) {
    return state.status;
  }
  if (state.status === "won") {
    return "lost";
  }
  const level =
    runEnding(state) === "clock" &&
    sideLand(state, kingdomsOfSide(state, faction)) ===
      sideLand(state, kingdomsOfSide(state, "player"));
  return level ? "lost" : "won";
}

/** Every kingdom in the run on another side than the player's, in resolution order. */
export function rivalsOf(state: RunState): FactionId[] {
  return state.factions.filter((faction) => hostile(state.rules, faction, "player"));
}

/** The run ended by surrender rather than elimination or the clock; the HUD names it. */
export function enemySurrendered(state: RunState): boolean {
  return state.status === "won" && state.enemyDespairTicks >= state.rules.surrenderTicks;
}

/** A faction's army as one number: the HP of every unit it fields. */
export function totalStrength(state: RunState, faction: FactionId): number {
  let total = 0;
  for (const unit of state.units) {
    if (unit.owner === faction) {
      total += unit.hp;
    }
  }
  return total;
}

/**
 * Hopeless: the enemy side holds at most `surrenderHexShare` of the player's side's hexes and at
 * most `surrenderStrengthShare` of its total unit HP, both at once. Held for `surrenderTicks` in a
 * row it becomes a surrender (Increment 3.15), the whole side's (K6). Never applied to the
 * player: a human's exit is the restart, not a rule.
 */
export function enemyHopeless(state: RunState): boolean {
  const [rival, ...others] = rivalSidesOf(state);
  // Surrender is the duel's rule (ADR-0012): with several rival sides a kingdom falls with its capital (W4).
  // A side a human sits in plays on: giving up is a human's choice, not a rule's (B3.3, K1).
  const held = heldFactions(state);
  if (rival === undefined || others.length > 0 || rival.some((kingdom) => held.includes(kingdom))) {
    return false;
  }
  const own = kingdomsOfSide(state, "player");
  const strengthOf = (side: readonly FactionId[]): number =>
    side.reduce((sum, kingdom) => sum + totalStrength(state, kingdom), 0);
  return (
    sideLand(state, rival) <= sideLand(state, own) * state.rules.surrenderHexShare &&
    strengthOf(rival) <= strengthOf(own) * state.rules.surrenderStrengthShare
  );
}

/** Step 5: the despair counter climbs while the enemy is hopeless and drops to zero otherwise. */
export function trackDespair(state: RunState): RunState {
  const enemyDespairTicks = enemyHopeless(state) ? state.enemyDespairTicks + 1 : 0;
  return enemyDespairTicks === state.enemyDespairTicks ? state : { ...state, enemyDespairTicks };
}

export function settle(state: RunState, landChanged: boolean): RunState {
  const status = evaluateStatus(state, landChanged);
  return status === state.status ? state : { ...state, status };
}
